import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR, fmtDate } from "@/lib/roles";
import { Plus, Loader2, Building, Search } from "lucide-react";

const STATUS_LABELS = {
  submitted: { label: "Submitted", tone: "bg-slate-100 text-slate-700 border-slate-300" },
  under_scrutiny: { label: "Under Scrutiny", tone: "bg-blue-100 text-blue-700 border-blue-300" },
  inspection_scheduled: { label: "Inspection Scheduled", tone: "bg-amber-100 text-amber-800 border-amber-300" },
  inspection_completed: { label: "Inspection Done", tone: "bg-purple-100 text-purple-700 border-purple-300" },
  needs_revision: { label: "Needs Revision", tone: "bg-orange-100 text-orange-700 border-orange-300" },
  approved: { label: "Approved", tone: "bg-green-100 text-green-700 border-green-300" },
  rejected: { label: "Rejected", tone: "bg-red-100 text-red-700 border-red-300" },
};

export function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, tone: "bg-slate-100 text-slate-700" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${s.tone}`}>{s.label}</span>;
}

export default function BuildingPlansList() {
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const load = () => {
    setLoading(true);
    const params = { page, limit };
    if (status !== "all") params.status = status;
    api.get("/building-plans", { params }).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page, status]);

  const newPath = isCitizen ? "/portal/building-plans/new" : "/building-plans/new";
  const detailPath = isCitizen ? "/portal/building-plans" : "/building-plans";

  return (
    <div className="space-y-6" data-testid="building-plans-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Building Plan Approval</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <Building className="w-6 h-6 text-[#0F2942] dark:text-white" /> Building Plans
          </h1>
        </div>
        <Link to={newPath}>
          <Button className="bg-[#0F2942] hover:bg-[#1E3A5F]" data-testid="btn-new-application">
            <Plus className="w-4 h-4 mr-1" /> New application
          </Button>
        </Link>
      </div>

      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <div className="text-xs uppercase font-semibold tracking-wider text-slate-500">Filter</div>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="w-64" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No applications found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="building-plans-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-3 px-4">Application</th>
                    <th className="text-left py-3 px-4">Applicant</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Purpose / Type</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Ward</th>
                    <th className="text-right py-3 px-4">Fee</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4 hidden xl:table-cell">Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-3 px-4 font-mono text-xs">{a.application_id}</td>
                      <td className="py-3 px-4">
                        <div className="font-medium">{a.applicant_name}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{a.address}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell capitalize">
                        <div>{a.purpose?.replace("_", " ")}</div>
                        <div className="text-xs text-slate-500">{a.property_type}</div>
                      </td>
                      <td className="py-3 px-4 hidden lg:table-cell">{a.ward}</td>
                      <td className="py-3 px-4 text-right font-mono">{formatINR(a.fee_amount)}</td>
                      <td className="py-3 px-4"><StatusBadge status={a.status} /></td>
                      <td className="py-3 px-4 hidden xl:table-cell text-xs text-slate-500">{fmtDate(a.created_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <Link to={`${detailPath}/${a.id}`}>
                          <Button size="sm" variant="ghost" data-testid={`view-bp-${a.application_id}`}>View</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button size="sm" variant="outline" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
